/**
 * Settings › Sandbox › "Sandbox type": the sandbox types (templates) the person's plan offers, each with what it gives
 * (CPU, memory, code and data disk, Docker, its languages and databases), the one the next start runs marked Current
 * and the one running now (vfs-service src/gateway/sandboxTemplates/types.js, services/MySandboxService.js).
 * Picking another asks the gateway what it would cost first: a switch it refuses (a disk smaller than the files) is
 * said and not offered; warnings (no Docker, languages or databases missing) are listed and need a confirmation. The
 * switch applies at the next start of the sandbox; the disks are kept. While the chosen type is not the one running,
 * "Switch now" puts the sandbox to sleep and wakes it with it (about a minute; previews and chats interrupted; files and
 * data kept; the gateway's .../manage/switch-type-now). Shown only where the plan offers more than one
 * type, or where the person's choice is still pending or was dropped by a plan change.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Button } from 'antd';
import Modal from '../Modal';
import { checkMySandboxType, getMySandboxType, setMySandboxType, switchMySandboxTypeNow } from '../../services/MySandboxService';
import { formatBytes } from '../../utils/dataModel';
import './SandboxTypePicker.css';

const STORE_LABELS = { postgres: 'PostgreSQL', mariadbd: 'MariaDB', mongod: 'MongoDB', 'redis-server': 'Redis', neo4j: 'Neo4j', sqlite3: 'SQLite', kafka: 'Kafka' };
const TOOL_SKIP = new Set(['git', 'npm', 'pip3', 'make']);

const cores = (value) => `${Math.round(value * 100) / 100} vCPU`;

/** The resources line of a type: "4 vCPU · 8 GB memory · 20 GB code disk · 10 GB data disk · Docker". */
export function resourcesText(type) {
  const parts = [];
  parts.push(typeof type.cpu === 'number' ? cores(type.cpu) : 'CPU: no limit');
  parts.push(typeof type.memoryBytes === 'number' ? `${formatBytes(type.memoryBytes)} memory` : 'memory: no limit');
  parts.push(typeof type.diskBytes === 'number' ? `${formatBytes(type.diskBytes)} code disk` : 'code disk: no limit');
  if (typeof type.dataDiskBytes === 'number') { parts.push(`${formatBytes(type.dataDiskBytes)} data disk`); }
  parts.push(type.docker ? 'Docker' : 'no Docker');
  return parts.join(' · ');
}

/** What the type's image runs: "Java 21, Maven · PostgreSQL 15.4", or every language for Flow's own image. */
export function runsText(type) {
  if (!type.tools && !type.datastores) { return 'Every language, framework and database Flow supports.'; }
  const tools = Object.entries(type.tools || {}).filter(([name]) => !TOOL_SKIP.has(name)).map(([name, version]) => (version && version !== true ? `${name} ${version}` : name));
  const stores = Object.entries(type.datastores || {}).map(([key, version]) => `${STORE_LABELS[key] || key}${version && version !== true ? ` ${version}` : ''}`);
  return [tools.length ? tools.join(', ') : 'no language toolchain', stores.length ? stores.join(', ') : 'no databases'].join(' · ');
}

/** Whether the picker shows: more than one type to pick, or a choice still pending / dropped. */
export function showPicker(view) {
  if (!view || !Array.isArray(view.types)) { return false; }
  return view.types.length > 1 || Boolean(view.appliesAtNextStart) || Boolean(view.fallback);
}

function TypeCard({ type, selected, onSelect, disabled }) {
  const unavailable = !type.allowed || !type.ready;
  return (
    <label className={`stp-card${selected ? ' stp-card-selected' : ''}${unavailable ? ' stp-card-off' : ''}`}>
      <input
        checked={selected}
        disabled={disabled || unavailable}
        name="sandbox-type"
        onChange={() => onSelect(type.id)}
        type="radio"
        value={type.id}
      />
      <span className="stp-card-body">
        <span className="stp-card-head">
          <strong>{type.name}</strong>
          {type.current && <span className="stp-tag stp-tag-current">Current</span>}
          {type.running && !type.current && <span className="stp-tag">Running now</span>}
          {type.default && <span className="stp-tag">Plan’s default</span>}
          {!type.allowed && <span className="stp-tag stp-tag-off">No longer in your plan</span>}
          {type.allowed && !type.ready && <span className="stp-tag stp-tag-off">Not available yet</span>}
        </span>
        {type.description && <span className="stp-desc">{type.description}</span>}
        <span className="stp-meta">{resourcesText(type)}</span>
        <span className="stp-meta">{runsText(type)}</span>
      </span>
    </label>
  );
}

TypeCard.propTypes = {
  type: PropTypes.object.isRequired,
  selected: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

/** Whether "Switch now" is offered: the chosen type is not the one the pod runs. */
export function switchPending(view) {
  return Boolean(view && view.appliesAtNextStart && view.running && view.running !== view.current);
}

export default function SandboxTypePicker({ load = getMySandboxType, check = checkMySandboxType, save = setMySandboxType, switchNow = switchMySandboxTypeNow, onChanged = null }) {
  const [view, setView] = useState(null);
  const [picked, setPicked] = useState(null);
  const [verdict, setVerdict] = useState(null); // { type, blockers, warnings } of the picked type
  const [confirming, setConfirming] = useState(false);
  const [switching, setSwitching] = useState(false); // the "Switch now" confirmation is open
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // { tone, text }

  const refresh = useCallback(() => load().then((next) => { setView(next); setPicked(next ? next.current : null); return next; }).catch(() => null), [load]);
  useEffect(() => { refresh(); }, [refresh]);

  if (!showPicker(view)) { return null; }
  const nameOf = (id) => (view.types.find((t) => t.id === id) || { name: id }).name;
  const changed = picked && picked !== view.current;

  const review = async() => {
    setBusy(true);
    setStatus(null);
    try {
      const out = await check(picked);
      setVerdict(out);
      if (out.blockers && out.blockers.length) {
        setStatus({ tone: 'error', text: out.blockers.map((b) => b.message).join(' ') });
      } else {
        setConfirming(true);
      }
    } catch (err) {
      setStatus({ tone: 'error', text: err.message || 'The sandbox type could not be checked.' });
    } finally {
      setBusy(false);
    }
  };

  const confirm = async() => {
    setBusy(true);
    try {
      const out = await save(picked, { confirm: true });
      setConfirming(false);
      setView(out);
      setPicked(out.current);
      setStatus({ tone: 'ok', text: switchPending(out)
        ? `${nameOf(out.current)} from the next start of your sandbox. Your projects and data stay. Switch now to use it at once.`
        : `${nameOf(out.current)} from the next start of your sandbox. Your projects and data stay.` });
      if (onChanged) { onChanged(out); }
    } catch (err) {
      setConfirming(false);
      setStatus({ tone: 'error', text: err.message || 'The sandbox type could not be changed.' });
    } finally {
      setBusy(false);
    }
  };

  const applyNow = async() => {
    setBusy(true);
    try {
      const out = await switchNow();
      setSwitching(false);
      setStatus({ tone: 'ok', text: out && out.state === 'running'
        ? `Your sandbox now runs ${nameOf(view.current)}.`
        : `Your sandbox is waking up with ${nameOf(view.current)}: about a minute.` });
      await refresh();
      if (onChanged) { onChanged(out); }
    } catch (err) {
      setSwitching(false);
      setStatus({ tone: 'error', text: err.message || 'The sandbox could not be switched now.' });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const pendingNow = switchPending(view);
  const pending = pendingNow
    ? `${nameOf(view.current)} from the next start; ${nameOf(view.running)} runs until your sandbox sleeps, or switch now.`
    : null;
  return (
    <div aria-labelledby="set-sandbox-type" className="msb-section" data-testid="sandbox-type" role="group">
      <h3 id="set-sandbox-type">Sandbox type</h3>
      <p className="settings-help">
        What your sandbox runs on, among the types your plan offers. A switch applies the next time your sandbox starts; your disks, projects and data stay.
      </p>
      {view.fallback === 'not_allowed' && (
        <p className="msb-status msb-status-info" role="status">
          {`The type you had picked (${nameOf(view.chosen)}) is no longer in your plan: your sandbox starts with ${nameOf(view.current)}.`}
        </p>
      )}
      {pending && <p className="msb-status msb-status-info" role="status">{pending}</p>}
      <div aria-label="Sandbox types" className="stp-list" role="radiogroup">
        {view.types.map((type) => (
          <TypeCard
            disabled={busy}
            key={type.id}
            onSelect={(id) => { setPicked(id); setStatus(null); setVerdict(null); }}
            selected={picked === type.id}
            type={type}
          />
        ))}
      </div>
      <div className="stp-actions">
        <Button disabled={!changed || busy} loading={busy && !confirming} onClick={review} type="primary">
          {changed ? `Switch to ${nameOf(picked)}…` : 'Switch type…'}
        </Button>
        {changed && <Button disabled={busy} onClick={() => { setPicked(view.current); setStatus(null); }}>Cancel</Button>}
        {pendingNow && !changed && (
          <Button disabled={busy} onClick={() => setSwitching(true)}>Switch now…</Button>
        )}
      </div>
      {status && <p className={`msb-status msb-status-${status.tone}`} role={status.tone === 'error' ? 'alert' : 'status'}>{status.text}</p>}
      <Modal
        cancelText="Keep current type"
        className="msb-dialog"
        confirmText="Switch at next start"
        focusCancel
        isLoading={busy}
        isOpen={confirming}
        onCancel={() => setConfirming(false)}
        onClose={busy ? null : () => setConfirming(false)}
        onConfirm={confirm}
        title={`Switch to ${nameOf(picked)}?`}
        type="custom"
        withFooter
      >
        <div className="msb-body">
          <p className="msb-label">What happens</p>
          <ul className="msb-list">
            <li>{`Your sandbox keeps running ${nameOf(view.running || view.current)} until it next starts (or until you switch now, next); then it runs ${nameOf(picked)}.`}</li>
            <li>Your code and data disks, projects and settings stay.</li>
          </ul>
          {verdict && verdict.warnings && verdict.warnings.length > 0 && (
            <>
              <p className="msb-label">Before you switch</p>
              <ul className="msb-list stp-warnings">
                {verdict.warnings.map((w) => <li key={w.code}>{w.message}</li>)}
              </ul>
            </>
          )}
        </div>
      </Modal>
      <Modal
        cancelText="Not now"
        className="msb-dialog"
        confirmText="Switch now"
        focusCancel
        isLoading={busy}
        isOpen={switching}
        onCancel={() => setSwitching(false)}
        onClose={busy ? null : () => setSwitching(false)}
        onConfirm={applyNow}
        title={`Switch to ${nameOf(view.current)} now?`}
        type="custom"
        withFooter
      >
        <div className="msb-body">
          <p className="msb-label">What happens</p>
          <ul className="msb-list">
            <li>{`Your sandbox goes to sleep and wakes with ${nameOf(view.current)}. It takes about a minute.`}</li>
            <li>Open previews and chats are interrupted; open them again once it is up.</li>
          </ul>
          <p className="msb-label">What stays</p>
          <ul className="msb-list">
            <li>Your files and data, projects and settings.</li>
          </ul>
        </div>
      </Modal>
    </div>
  );
}

/**
 * Home's sandbox card: the type the next start runs, as a pill next to the plan's, linking to Settings › Sandbox type.
 * Only where the plan offers more than one (`sandbox.plan.sandboxType`, the gateway's sandbox_status summary).
 */
export function SandboxTypePill({ sandbox = null }) {
  const type = sandbox?.plan?.sandboxType;
  if (!type || !(type.choices > 1 || type.fallback)) { return null; }
  return (
    <Link className="sbx-pill stp-pill" title="Your sandbox type: switch it in Settings › Sandbox" to="/settings#set-sandbox-type">
      {type.name}
    </Link>
  );
}

SandboxTypePill.propTypes = { sandbox: PropTypes.object };

SandboxTypePicker.propTypes = {
  load: PropTypes.func,
  check: PropTypes.func,
  save: PropTypes.func,
  switchNow: PropTypes.func,
  onChanged: PropTypes.func,
};
